/**
 * Main script for getting a value from either local (local.js), dynamic (ETCD),
 * or static (node-config) configurations based on that order of precedence.
 * @module
 * @example
 * var config = require('lc-app-config');
 *
 * // Returns matching value or `undefined` if no matches found.
 * console.log(config.get('key'));
 */

const { join } = require('path');
const objectPath = require('object-path');
const config = require('lc-config');
const { internalLogger } = require('internal-logger');
const ETCD = require('./etcd');
const etcdConfig = require('./default-config');
const MODULE_NAME = require('./module-name');

// NOTE: `local.js` is only used for local development purposes. Be careful not
// to include additional `local.EXT` as part of this as you may run into
// unexpected results since lc-bootstrap – which is used for all applications –
// applies all ETCD data to local.json once the application is deployed to an
// environment. If for instance we include, local.json as part of our precedence
// above our ETCD, we would never get those dynamic values if local.json has it.
const localConfig = objectPath(
  config.util.parseFile(join(process.cwd(), '/config/local.js'))
);

// Only allow one etcd instance so we don't have multiple local caches when
// modules require different versions.
let etcd;
/* eslint-disable no-underscore-dangle */
if (!global._etcdClient) {
  global._etcdClient = ETCD({
    environment: config.has('LC_PROFILE')
      ? config.get('LC_PROFILE')
      : undefined,
    subEnvironment: config.has('LC_SUB_PROFILE')
      ? config.get('LC_SUB_PROFILE')
      : undefined,
    appId: config.has('LC_APP_ID') ? config.get('LC_APP_ID') : undefined,
  });
  etcd = global._etcdClient;
  // Initial load of ETCD
  etcd.refresh().catch((err) => {
    internalLogger.debug({ err }, `${MODULE_NAME}: Catching promise rejection`);
  });

  // Poll the ETCD service for real-time configurations
  const timer = setInterval(etcd.refresh, etcdConfig.etcdClient.refreshTime);
  timer.unref();
} else {
  etcd = global._etcdClient;
}
/* eslint-enable no-underscore-dangle */

function getFromStaticConfig(path) {
  // Config get() method will throw an error if it's not there so check first
  if (!config.has(path)) {
    return undefined;
  }
  return config.get(path);
}

/**
 * Provide proper value lookup between config priorities. Priorities are as
 * follows:
 *
 * 1. local.js – Local configuration only.
 * 2. dynamic – Polled ETCD config. NOTE: This should always take the highest
 * precedence except to `config/local.js` – which should ONLY be used for local
 * development purposes. @see {@link module:src/etcd}
 * 3. static – All merged config files handled by lc-config. NOTE: This
 * includes local.json that automatically gets generated (with lc.groovy and a
 * snapshot of ETCD's content) during an application's deployment via
 * lc-bootstrap.
 *
 * @function get
 * @param path {String} Any valid path key lookup for an object. i.e. `someKey`,
 * `some.key`, `some-key`, etc...
 * @returns {*} The matched value of the object from the provided `path`
 * argument.
 * @example
 * // Local config:
 * {
 *  one: 'won',
 * };
 *
 * // ETCD config:
 * {
 *  one: 1,
 *  twenty-two: 22
 * };
 *
 * // Static merged config:
 * {
 *  one: 'one',
 *  twenty-two: 'twenty-two'
 *  static: true
 * };
 *
 * const config = require('lc-app-config');
 * config.get('one');         // returns 'won' (local config wins precedence)
 * config.get('twenty-two');  // returns 22 (ETCD config wins precedence)
 * config.get('static');      // returns true (static config wins precedence)
 */
function get(path) {
  if (typeof path !== 'string') {
    internalLogger.warn(
      { path },
      `${MODULE_NAME}: get() requires a 'path' argument.`
    );
    return undefined;
  }

  const localValue = localConfig.get(path);
  // We explicitly test for undefined to distinguish falsy values.
  if (localValue !== undefined) {
    internalLogger.trace({ path }, `${MODULE_NAME}: retrieving from local.js.`);
    return localValue;
  }

  const dynamicValue = etcd.get(path);
  // We explicitly test for undefined to distinguish falsy values
  if (dynamicValue !== undefined) {
    internalLogger.trace(
      { path },
      `${MODULE_NAME}: retrieving from dynamic etcd.`
    );
    return dynamicValue;
  }

  const staticValue = getFromStaticConfig(path);
  // We explicitly test for undefined to distinguish falsy values
  if (staticValue !== undefined) {
    internalLogger.trace(
      { path },
      `${MODULE_NAME}: retrieving from static config.`
    );
    return staticValue;
  }

  internalLogger.warn(
    { path },
    `${MODULE_NAME}: can not find dynamic or static config value for path.`
  );

  // Explicitly return undefined
  return undefined;
}

/**
 * Verifies if a value exists across all config layers. Priorities are as
 * follows:
 *
 * 1. local.js – Local configuration only.
 * 2. dynamic – Polled ETCD config. NOTE: This should always take the highest
 * precedence except to `config/local.js` – which should ONLY be used for local
 * development purposes. @see {@link module:src/etcd}
 * 3. static – All merged config files handled by lc-config. NOTE: This
 * includes local.json that automatically gets generated (with lc.groovy and a
 * snapshot of ETCD's content) during an application's deployment via
 * lc-bootstrap.
 *
 * @function has
 * @param path {String} Any valid path key lookup for an object. i.e. `someKey`,
 * `some.key`, `some-key`, etc...
 * @returns {*} true if there is a value found in a config layer, false otherwise,
 * undefined when `path` is other than a string.
 * @example
 * // Local config:
 * {
 *  one: 'won',
 * };
 *
 * // ETCD config:
 * {
 *  one: 1,
 *  twenty-two: 22
 * };
 *
 * // Static merged config:
 * {
 *  one: 'one',
 *  twenty-two: 'twenty-two'
 *  static: true
 * };
 *
 * const config = require('lc-app-config');
 * config.has('one');         // returns `true` (local config wins precedence)
 * config.has('twenty-two');  // returns `true` (ETCD config wins precedence)
 * config.has('static');      // returns `true` (static config wins precedence)
 * config.has('other');       // returns `false` (not found)
 * config.has(22);            // returns `undefined` (bad user call)
 */
function has(path) {
  if (typeof path !== 'string') {
    internalLogger.warn(
      { path },
      `${MODULE_NAME}: get() requires a 'path' argument.`
    );
    return undefined;
  }

  return localConfig.has(path) || etcd.has(path) || config.has(path);
}

/**
 * Provides a mechanism to look up (dynamic) ETCD properties and decrypt them in PEG
 *
 * @async
 * @function getEncrypted
 * @param path Any valid path key lookup for an ETCD property
 * @return {Promise<String|Number|Boolean>} The matched value of the object from
 * the provided `path` argument.
 * @example
 * // lc-merged.properties
 * MY_API_KEY=xxxxxx
 *
 * const config = require('lc-app-config');
 * await config.getEncrypted('MY_API_KEY');         // returns 'abc123'
 */
async function getEncrypted(path) {
  return etcd.getEncrypted(path);
}

module.exports = {
  get,
  getEncrypted,
  has,
  refresh: etcd.refresh,
};

